import { useI18n } from '../i18n';
import { BrandMark, type BrandMarkVariant } from '../ui';

export interface BrandLogoProps {
  /**
   * `inline` is the header's single line — mark then wordmark. `stacked` is
   * the footer's vertical composition, which only a block with that much
   * height to spend can afford.
   */
  orientation?: 'inline' | 'stacked' | undefined;
  /** Which cut of the mark. See `BrandMark` — `solid` below roughly 48px. */
  markVariant?: BrandMarkVariant | undefined;
  /** Sizing for the mark; the wordmark takes its size from the text context. */
  markClassName?: string | undefined;
  className?: string | undefined;
}

/**
 * The Bitwize lockup: the mark, and the wordmark beside or beneath it.
 *
 * The wordmark is live text, not artwork. That is what lets it stay
 * translatable, selectable and searchable, and what keeps it set in the site's
 * own loaded face — an `<img>` cannot load a webfont, so a wordmark baked into
 * an SVG would render in whatever fallback the visitor happened to have. It
 * also means there is one accessible name here rather than an image `alt`
 * competing with the text beside it.
 *
 * One component rather than the arrangement Header and Footer had before, where
 * the header hand-split the brand name and the footer used a PNG with the
 * wordmark burned into it — two lockups that could drift apart, and did.
 */
export function BrandLogo({
  orientation = 'inline',
  markVariant = 'solid',
  markClassName,
  className,
}: BrandLogoProps) {
  const { t } = useI18n();

  /*
    The wordmark is two-toned — "bit" in the body ink, "wize" in the accent,
    exactly as the approved logo art sets it. Split off the brand name rather
    than hard-coded, so the accessible name stays the one string translators
    own; a name that does not contain "wize" (a rebrand, a fork) simply renders
    whole and untinted instead of breaking.
  */
  const brandName = t('common.brand.name');
  const wizeAt = brandName.toLowerCase().indexOf('wize');

  return (
    <span
      className={
        (orientation === 'inline'
          ? 'flex shrink-0 items-center gap-2'
          : 'flex flex-col items-start gap-3') + (className ? ` ${className}` : '')
      }
    >
      <BrandMark variant={markVariant} className={markClassName} />
      {wizeAt > 0 ? (
        <span>
          {brandName.slice(0, wizeAt)}
          <b className="font-bold text-accent">{brandName.slice(wizeAt)}</b>
        </span>
      ) : (
        <span>{brandName}</span>
      )}
    </span>
  );
}
